import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";
import {
  createCourse,
  CreateCourseRequest,
} from "@/modules/learningCenter/features/CourseManagement/api/createCourse";
import {
  updateCourse,
  UpdateCourseRequest,
} from "@/modules/learningCenter/features/CourseManagement/api/updateCourse";
import { getCourse } from "@/modules/learningCenter/features/CourseManagement/api/getCourse";
import { Course } from "@/modules/learningCenter/features/CourseManagement/types/course";

interface CreateUpdateCourseState {
  loadingCreate: boolean;
  createdCourse: Course | null;

  loadingGet: boolean;
  getError: string | null;
  fetchedCourse: Course | null;

  loadingUpdate: boolean;
  updatedCourse: Course | null;
}

const initialState: CreateUpdateCourseState = {
  loadingCreate: false,
  createdCourse: null,

  loadingGet: false,
  getError: null,
  fetchedCourse: null,

  loadingUpdate: false,
  updatedCourse: null,
};

const createUpdateCourseSlice = createSlice({
  name: "createUpdateCourse",
  initialState,
  reducers: {
    setLoadingCreate(state, action: PayloadAction<boolean>) {
      state.loadingCreate = action.payload;
    },
    setCreatedCourse(state, action: PayloadAction<Course | null>) {
      state.createdCourse = action.payload;
    },
    setLoadingGet(state, action: PayloadAction<boolean>) {
      state.loadingGet = action.payload;
    },
    setGetError(state, action: PayloadAction<string | null>) {
      state.getError = action.payload;
    },
    setFetchedCourse(state, action: PayloadAction<Course | null>) {
      state.fetchedCourse = action.payload;
    },
    setLoadingUpdate(state, action: PayloadAction<boolean>) {
      state.loadingUpdate = action.payload;
    },
    setUpdatedCourse(state, action: PayloadAction<Course | null>) {
      state.updatedCourse = action.payload;
    },
    resetCreateUpdateCourseState(state) {
      state.loadingCreate = false;
      state.createdCourse = null;
      state.loadingGet = false;
      state.getError = null;
      state.fetchedCourse = null;
      state.loadingUpdate = false;
      state.updatedCourse = null;
    },
  },
});

export const {
  setLoadingCreate,
  setCreatedCourse,
  setLoadingGet,
  setGetError,
  setFetchedCourse,
  setLoadingUpdate,
  setUpdatedCourse,
  resetCreateUpdateCourseState,
} = createUpdateCourseSlice.actions;

export default createUpdateCourseSlice.reducer;

export const createCourseAction =
  (
    requestData: CreateCourseRequest,
    onSuccess?: (course: Course) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingCreate(true));
    try {
      const response = await createCourse(requestData);
      dispatch(setCreatedCourse(response.data));
      if (onSuccess) {
        onSuccess(response.data);
      }
    } finally {
      dispatch(setLoadingCreate(false));
    }
  };

export const getCourseAction =
  (uuid: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingGet(true));
    dispatch(setGetError(null));
    try {
      const response = await getCourse(uuid);
      dispatch(setFetchedCourse(response.data));
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to fetch the course.";
      dispatch(setGetError(message));
    } finally {
      dispatch(setLoadingGet(false));
    }
  };

export const updateCourseAction =
  (
    uuid: string,
    requestData: UpdateCourseRequest,
    onSuccess?: (course: Course) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingUpdate(true));
    try {
      const response = await updateCourse(uuid, requestData);
      dispatch(setUpdatedCourse(response.data));
      if (onSuccess) {
        onSuccess(response.data);
      }
    } finally {
      dispatch(setLoadingUpdate(false));
    }
  };
